import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'heviton.theme';

/**
 * 저장값이 없으면 **light** — OS 추종(`system`)이 기본이면 다크 환경 사용자가 의도치 않게 다크로 진입한다.
 * `system` 은 사용자가 명시 선택했을 때만 유효한 모드다. boot script(index.html)의 폴백과 같은 값이어야 한다.
 */
function readPersistedMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'light';
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'light';
}

function persistMode(mode: ThemeMode): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, mode);
}

function resolveDataTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

function applyThemeClass(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveDataTheme(mode);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/**
 * light/dark/system 테마 store.
 *
 * heritage-ui 의 `@custom-variant dark (&:is(.dark *))` 와 정합되도록 `.dark`
 * 클래스 토글 방식 사용. index.html 의 paint-전 boot script 가 같은 STORAGE_KEY
 * ('heviton.theme') 에서 raw 문자열을 읽어 FOUC 를 막는다. zustand persist 대신
 * raw 문자열 저장으로 boot script 와의 SSOT 를 유지한다.
 */
export const useThemeStore = create<ThemeState>()((set) => ({
  mode: readPersistedMode(),
  setMode: (mode) => {
    set({ mode });
    persistMode(mode);
    applyThemeClass(mode);
  },
}));

/** 지금 화면에 적용된 테마(`system` 은 OS 로 해석). 표면 색을 따라가야 하는 소비처용. */
export function getResolvedTheme(): 'light' | 'dark' {
  return resolveDataTheme(useThemeStore.getState().mode);
}

/**
 * **해석된 테마**(light|dark) 변화 구독 — 반환값은 해제 함수.
 *
 * 두 신호원을 여기서 합류시킨다: ① 사용자의 모드 변경(store) ② `system` 모드에서 OS 다크모드 전환(matchMedia).
 * 소비처(예: native 셸 동기화)가 두 곳을 각각 구독하면 판정 로직이 흩어지므로 store 가 한 번에 정리해 준다.
 * 해석 결과가 실제로 바뀔 때만 통지한다(light→light 같은 무의미한 통지 제거).
 */
export function subscribeResolvedTheme(listener: (theme: 'light' | 'dark') => void): () => void {
  let last = getResolvedTheme();
  const emit = () => {
    const next = getResolvedTheme();
    if (next === last) return;
    last = next;
    listener(next);
  };
  const unsubscribeStore = useThemeStore.subscribe(emit);
  const mql = typeof matchMedia !== 'undefined' ? matchMedia('(prefers-color-scheme: dark)') : null;
  mql?.addEventListener('change', emit);
  return () => {
    unsubscribeStore();
    mql?.removeEventListener('change', emit);
  };
}

/** system 모드일 때 OS 다크모드 변경에 즉시 반응. */
export function bindSystemThemeListener(): () => void {
  if (typeof matchMedia === 'undefined') return () => {};
  const mql = matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    const { mode } = useThemeStore.getState();
    if (mode === 'system') applyThemeClass('system');
  };
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}
